import { FormEvent } from "react"
import { Head, useForm, usePage } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { DEFAULT_TIMEZONE } from "@/lib/datetime"
import { TIMEZONE_OPTIONS } from "@/lib/timezones"
import { ProfileSubNav } from "./ProfileSubNav"

import type { PageProps } from "@/types/inertia"

export default function ProfileDetails() {
  const { props } = usePage<PageProps>()
  const user = props.current_user
  const errors = props.errors ?? {}

  const detailsForm = useForm({
    email: user?.email ?? "",
    timezone: user?.timezone ?? DEFAULT_TIMEZONE,
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    detailsForm.patch("/profile/details", { preserveScroll: true })
  }

  return (
    <>
      <Head title="My details">
        <meta
          name="description"
          content="Update the email address you use to log in and the timezone you see times in."
        />
        <meta property="og:title" content="My details" />
        <meta
          property="og:description"
          content="Update the email address you use to log in and the timezone you see times in."
        />
      </Head>
      <AppShell>
        <PageHeader
          title="Profile"
          description="Manage your account."
          tabs={<ProfileSubNav active="details" />}
        />

        {props.flash?.notice && (
          <p className="mt-6 text-sm text-accent">{props.flash.notice}</p>
        )}

        <section className="mt-10 max-w-md">
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="email">Email</label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                aria-invalid={!!errors.email}
                value={detailsForm.data.email}
                onChange={(e) => detailsForm.setData("email", e.target.value)}
              />
              {errors.email && (
                <p className="text-xs text-danger-display">{errors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <label htmlFor="timezone">Timezone</label>
              <Select
                id="timezone"
                required
                aria-invalid={!!errors.timezone}
                value={detailsForm.data.timezone}
                onChange={(e) => detailsForm.setData("timezone", e.target.value)}
              >
                {TIMEZONE_OPTIONS.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </Select>
              <p className="text-xs text-ink-muted">
                All times across SparkDrop display in this timezone.
              </p>
              {errors.timezone && (
                <p className="text-xs text-danger-display">{errors.timezone}</p>
              )}
            </div>

            <Button type="submit" disabled={detailsForm.processing}>
              Save profile
            </Button>
          </form>
        </section>
      </AppShell>
    </>
  )
}
